/**
 * The Station panel's view-model (M4 spec §12.4, §15 item 4): owner and template, production lines with their
 * status and missing input ("Starved: needs Water"), the workforce multiplier, habitat satisfaction and the
 * shipyard queue; M5's state banner on top; RR-F1 BL-2a's traffic lines (the ships waiting on the station). Statuses carry a word and a glyph, never colour alone. UI events only.
 */
import type { LineStatusName, StationView } from '../../strategic/types'
import { factionName, hullName, recipeName, templateName, wareName } from './names'

export const LINE_STATUS_LABELS: Readonly<Record<LineStatusName, string>> = Object.freeze({
  running: 'Running',
  starved: 'Starved',
  blocked: 'Blocked',
  idle: 'Idle',
})

const LINE_STATUS_GLYPHS: Readonly<Record<LineStatusName, string>> = Object.freeze({
  running: '▶',
  starved: '⚠',
  blocked: '■',
  idle: '○',
})

/**
 * RR-F1 BL-2a (D-803 C4, D-828 BL-T (b)): the traffic lines, one per non-zero count of `StationView.miners`
 * ({n} is the count). No line for a zero count or an absent field.
 */
export const STATION_TRAFFIC_TEXT = Object.freeze({
  stoodDown: '{n} stood down: ore market full',
  booked: '{n} booked for here',
  haulersWaiting: '{n} haulers waiting for a cargo: buyers full',
})

const TRAFFIC_KEYS = ['stoodDown', 'booked', 'haulersWaiting'] as const

/** The station's traffic lines (BL-2a), in the order stood down, booked, haulers waiting. */
export function trafficLines(miners: StationView['miners']): string[] {
  const out: string[] = []
  if (miners === undefined) return out
  for (const k of TRAFFIC_KEYS) {
    const n = miners[k]
    if (n > 0) out.push(STATION_TRAFFIC_TEXT[k].replace('{n}', String(n)))
  }
  return out
}

/**
 * LV WK-1v (D5): the megaproject line, "Kestrel Ore Sling 3/5", only when the station has built a stage (`megaStage`
 * with `stage` > 0); null otherwise. D-1121 card A2: a captured one adds " · built by X, held by Y".
 */
export function megaprojectLine(mega: StationView['megaStage']): string | null {
  if (mega === undefined || mega === null || !(mega.stage > 0)) return null
  const line = `${mega.name} ${mega.stage}/${mega.of}`
  return mega.builtBy === undefined || mega.heldBy === undefined ? line : `${line} · built by ${mega.builtBy}, held by ${mega.heldBy}`
}

export type PanelTone = 'ok' | 'warning' | 'danger' | 'muted'

const LINE_TONES: Readonly<Record<LineStatusName, PanelTone>> = Object.freeze({
  running: 'ok',
  starved: 'danger',
  blocked: 'warning',
  idle: 'muted',
})

export interface LineRow {
  /** What the line makes. */
  label: string
  status: LineStatusName
  statusLabel: string
  glyph: string
  tone: PanelTone
  /** "Needs Water" (starved), "Output full" (blocked), or null. */
  explain: string | null
  /** 0..100. */
  progressPct: number
}

export interface StationPanelModel {
  title: string
  /** "Reach Charter Authority · Charter Hub". */
  subtitle: string
  banner: string | null
  lines: LineRow[]
  /** Starved lines as sentences ("Steel line starved: needs Ferrite"), for the summary. */
  problems: string[]
  workforce: string
  habitat: string | null
  shipyard: string[] | null
  /** RR-F1 BL-2a: "3 stood down: ore market full", "4 booked for here", "2 haulers waiting for a cargo: buyers full"; empty when none. */
  traffic: string[]
  /** LV WK-1v: "Kestrel Ore Sling 3/5" while the station has built a megaproject stage, else null. */
  megaproject: string | null
}

function explain(status: LineStatusName, missing: string | null): string | null {
  if (status === 'starved') return missing !== null ? `Needs ${wareName(missing)}` : 'Needs inputs'
  if (status === 'blocked') return 'Output storage full'
  return null
}

function etaLabel(eta: number | null): string {
  if (eta === null) return 'waiting for parts'
  const s = Math.max(0, Math.round(eta))
  const m = Math.floor(s / 60)
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`
}

export function stationPanelModel(view: StationView): StationPanelModel {
  const lines: LineRow[] = []
  const problems: string[] = []
  for (const l of view.lines) {
    const label = recipeName(l.recipe)
    const text = explain(l.status, l.missing)
    lines.push({
      label,
      status: l.status,
      statusLabel: LINE_STATUS_LABELS[l.status],
      glyph: LINE_STATUS_GLYPHS[l.status],
      tone: LINE_TONES[l.status],
      explain: text,
      progressPct: Math.round(Math.min(1, Math.max(0, l.progress)) * 100),
    })
    if (l.status === 'starved') problems.push(`${label} line starved: needs ${l.missing !== null ? wareName(l.missing) : 'inputs'}`)
  }
  const owner = view.holder !== null && view.holder !== view.owner ? `${factionName(view.owner)} (held by ${factionName(view.holder)})` : factionName(view.owner)
  return {
    title: view.name,
    subtitle: `${owner} · ${templateName(view.template)}`,
    banner: view.banner?.text ?? null,
    lines,
    problems,
    workforce: `×${view.workforceMult.toFixed(2)}`,
    habitat: view.habitat === null ? null : `${Math.round(view.habitat.satisfaction * 100)}% satisfied`,
    shipyard: view.shipyard === null ? null : view.shipyard.queue.map((q) => `${hullName(q.hull)} for ${factionName(q.faction)} · ${etaLabel(q.eta)}`),
    traffic: trafficLines(view.miners),
    megaproject: megaprojectLine(view.megaStage),
  }
}
